import Impostor from '../../rendering/Impostor.js';
import Transform from '../../math/Transform.js';
import Debug from '../../utils/Debug.js';

/**
 * ImpostorTool - Editor UI for placing and managing vegetation impostors.
 * Renders in the Edit tab sidebar.
 */
export default class ImpostorTool {
  /**
   * @param {Object} options
   * @param {World} options.world
   * @param {Camera} options.camera
   * @param {HTMLElement} options.container - Sidebar content container
   */
  constructor(options = {}) {
    this.world = options.world;
    this.camera = options.camera;
    this.container = options.container;
    this.editor = options.editor;
    this.facePicker = options.facePicker;
    this.toolbar = options.toolbar;

    // Placement state
    this._placing = false;
    this._previousTool = null;

    // Click detection (mousedown+mouseup, not click — more reliable on CSS 3D elements)
    this._mouseDownPos = null;
    this._CLICK_THRESHOLD = 5;

    // Manifest data
    this._manifest = null;
    this._selectedTexture = null;

    // Flip mode: 'random' (default), 'normal', or 'flipped'
    this._flipMode = 'random';

    // Selection state
    this._selectedImpostor = null;

    // Bound event handlers
    this._onMouseDownBound = this._onMouseDown.bind(this);
    this._onMouseUpBound = this._onMouseUp.bind(this);
    this._onTouchStartBound = this._onTouchStart.bind(this);
    this._onTouchEndBound = this._onTouchEnd.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);

    this._buildUI();
    this._loadManifest();

    // Listen for external impostor changes (from MoveTool/ExtendTool)
    window.addEventListener('impostors-changed', () => {
      this._deselectImpostor();
      this.refreshList();
    });

    // Cancel placement when user selects a cuboid tool directly
    window.addEventListener('editor-tool-changed', (e) => {
      if (this._placing && e.detail.tool !== null) {
        this._cancelPlacement(false);
      }
    });

    // Auto-enable placement when entering vegetation submenu, cancel when leaving
    window.addEventListener('toolbar-submenu-change', (e) => {
      if (e.detail.level === 'vegetation') {
        // Auto-start placement if a texture is selected (or select first available)
        if (this._selectedTexture) {
          this._startPlacement();
        } else if (this._manifest && this._manifest.categories?.length > 0) {
          const firstCat = this._manifest.categories[0];
          if (firstCat.textures?.length > 0) {
            this._selectTexture(firstCat.textures[0]);
            this._startPlacement();
          }
        }
      } else if (this._placing) {
        this._cancelPlacement(false);
      }
    });
  }

  /**
   * Load the vegetation manifest and populate the gallery
   * @private
   */
  async _loadManifest() {
    try {
      const response = await fetch('/textures/vegetation/vegetation-manifest.json');
      this._manifest = await response.json();
      this._populateCategories();
    } catch (error) {
      Debug.warn('ImpostorTool', 'Failed to load vegetation manifest:', error);
      this._statusText.textContent = 'Failed to load vegetation textures';
    }
  }

  /**
   * Populate category dropdown from manifest
   * @private
   */
  _populateCategories() {
    if (!this._manifest || !this._manifest.categories) return;

    this._categorySelect.innerHTML = this._manifest.categories.map(cat =>
      `<option value="${cat.id}">${cat.label}</option>`
    ).join('');

    this._onCategoryChange();
  }

  /**
   * Update the thumbnail gallery when category changes
   * @private
   */
  _onCategoryChange() {
    if (!this._manifest) return;

    const categoryId = this._categorySelect.value;
    const category = this._manifest.categories.find(c => c.id === categoryId);
    if (!category) return;

    let html = '';
    for (const tex of category.textures) {
      const isSelected = this._selectedTexture && this._selectedTexture.id === tex.id;
      html += `<div class="impostor-thumb${isSelected ? ' selected' : ''}" data-texture-id="${tex.id}" data-texture-path="${tex.path}" title="${tex.label}" style="width: 40px; height: 40px; display: inline-block; margin: 2px; border: 1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-color)'}; border-radius: 4px; cursor: pointer; background: url('${tex.thumbnail}') center/contain no-repeat var(--bg-surface);"></div>`;
    }
    this._gallery.innerHTML = html;

    // Attach click handlers
    this._gallery.querySelectorAll('.impostor-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const texId = thumb.dataset.textureId;
        const texPath = thumb.dataset.texturePath;
        const tex = category.textures.find(t => t.id === texId);
        this._selectTexture(tex);
      });
    });

    // Select first texture if none selected
    if (!this._selectedTexture && category.textures.length > 0) {
      this._selectTexture(category.textures[0]);
    }
  }

  /**
   * Select a texture from the gallery
   * @private
   */
  _selectTexture(tex) {
    this._selectedTexture = tex;

    // Update gallery highlight
    this._gallery.querySelectorAll('.impostor-thumb').forEach(thumb => {
      const isSelected = thumb.dataset.textureId === tex.id;
      thumb.style.borderColor = isSelected ? 'var(--accent-primary)' : 'var(--border-color)';
      thumb.classList.toggle('selected', isSelected);
    });

    // Update toolbar preview
    if (this.toolbar) {
      this.toolbar.updateVegPreview(tex.thumbnail);
    }
  }

  /**
   * Build the vegetation section in the sidebar
   * @private
   */
  _buildUI() {
    const section = document.createElement('div');
    section.id = 'impostorSection';
    section.className = 'gui-section';

    section.innerHTML = `
      <div class="gui-row gui-row-stacked" style="margin-bottom: 6px;">
        <label class="gui-label gui-label-stacked" style="color: var(--text-secondary); font-size: 12px;">Category</label>
        <select id="impostorCategorySelect" class="gui-select">
          <option value="">Loading...</option>
        </select>
      </div>

      <div id="impostorGallery" style="padding: 4px 0; max-height: 160px; overflow-y: auto;"></div>

      <div class="gui-row" style="margin-bottom: 4px;">
        <span class="gui-label" style="color: var(--text-secondary); font-size: 12px;">Scale</span>
        <div class="gui-slider-row">
          <input type="range" id="impostorScale" class="gui-slider" min="0.5" max="2.5" step="0.1" value="1">
          <input type="number" id="impostorScaleValue" class="gui-input gui-slider-value" value="1" step="0.1" min="0.5" max="2.5">
        </div>
      </div>

      <div class="gui-row" style="margin-bottom: 8px;">
        <span class="gui-label" style="color: var(--text-secondary); font-size: 12px;">Flip</span>
        <div id="impostorFlipGroup" class="toolbar-toggle-group" style="flex: 1;">
          <button class="toolbar-toggle-btn impostor-flip-btn active" data-flip="random" style="flex:1; font-size: 11px;">Random</button>
          <button class="toolbar-toggle-btn impostor-flip-btn" data-flip="normal" style="flex:1; font-size: 11px;">Normal</button>
          <button class="toolbar-toggle-btn impostor-flip-btn" data-flip="flipped" style="flex:1; font-size: 11px;">Flipped</button>
        </div>
      </div>

      <button id="impostorPlaceBtn" style="display:none;"></button>
      <button id="impostorCancelBtn" style="display:none;"></button>

      <div id="impostorStatusText" style="padding: 4px 0; font-size: 11px; color: var(--text-secondary);"></div>

      <div id="impostorSelectionPanel" style="display:none; padding: 4px 0;">
        <div style="font-size: 11px; color: var(--text-secondary); margin-bottom: 4px;">Selected: <span id="impostorSelectionName" style="color: var(--text-primary);"></span></div>
        <div class="gui-button-group">
          <button id="impostorApplyTexBtn" class="gui-button">Apply Texture</button>
          <button id="impostorDeleteSelBtn" class="gui-button gui-button-danger">Delete</button>
        </div>
      </div>

      <div id="impostorList"></div>
    `;

    this.container.appendChild(section);

    // Cache elements
    this._categorySelect = document.getElementById('impostorCategorySelect');
    this._gallery = document.getElementById('impostorGallery');
    this._scaleSlider = document.getElementById('impostorScale');
    this._scaleValue = document.getElementById('impostorScaleValue');
    this._placeBtn = document.getElementById('impostorPlaceBtn');
    this._cancelBtn = document.getElementById('impostorCancelBtn');
    this._statusText = document.getElementById('impostorStatusText');
    this._selectionPanel = document.getElementById('impostorSelectionPanel');
    this._selectionName = document.getElementById('impostorSelectionName');
    this._applyTexBtn = document.getElementById('impostorApplyTexBtn');
    this._deleteSelBtn = document.getElementById('impostorDeleteSelBtn');
    this._listEl = document.getElementById('impostorList');

    // Event listeners
    this._categorySelect.addEventListener('change', () => this._onCategoryChange());
    this._scaleSlider.addEventListener('input', (e) => {
      this._scaleValue.value = e.target.value;
    });
    this._scaleValue.addEventListener('input', (e) => {
      this._scaleSlider.value = e.target.value;
    });
    this._placeBtn.addEventListener('click', () => this._startPlacement());
    this._cancelBtn.addEventListener('click', () => this._cancelPlacement());
    this._applyTexBtn.addEventListener('click', () => this._applyTextureToSelected());
    this._deleteSelBtn.addEventListener('click', () => this._deleteSelected());

    // Flip toggle buttons
    this._flipButtons = document.querySelectorAll('#impostorFlipGroup .impostor-flip-btn');
    this._flipButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this._flipMode = btn.dataset.flip;
        this._flipButtons.forEach(b => {
          b.classList.toggle('active', b.dataset.flip === this._flipMode);
        });
      });
    });

    this.refreshList();
  }

  /**
   * Start placement mode
   * @private
   */
  _startPlacement() {
    if (!this._selectedTexture) {
      this._statusText.textContent = 'Select a texture first.';
      return;
    }

    this._placing = true;
    this._cancelBtn.style.display = '';
    this._placeBtn.style.display = 'none';
    this._statusText.textContent = 'Click a surface to place vegetation...';

    // Deactivate the active cuboid tool so clicks don't also add/move/extend cuboids
    if (this.editor) {
      this._previousTool = this.editor.activeTool?.name || null;
      this.editor.setTool(null);
    }
    // Disable face selection during placement
    if (this.facePicker) this.facePicker.setEnabled(false);

    // Listen for mouse/touch events
    const containerEl = document.getElementById('container');
    if (containerEl) {
      containerEl.addEventListener('mousedown', this._onMouseDownBound, true);
      containerEl.addEventListener('mouseup', this._onMouseUpBound, true);
      containerEl.addEventListener('touchstart', this._onTouchStartBound, { capture: true, passive: false });
      containerEl.addEventListener('touchend', this._onTouchEndBound, { capture: true, passive: false });
    }
    document.addEventListener('keydown', this._onKeyDownBound);
  }

  /**
   * Cancel placement mode
   * @private
   */
  _cancelPlacement(restoreTool = true) {
    this._placing = false;
    this._placeBtn.style.display = '';
    this._cancelBtn.style.display = 'none';
    this._statusText.textContent = '';

    const containerEl = document.getElementById('container');
    if (containerEl) {
      containerEl.removeEventListener('mousedown', this._onMouseDownBound, true);
      containerEl.removeEventListener('mouseup', this._onMouseUpBound, true);
      containerEl.removeEventListener('touchstart', this._onTouchStartBound, true);
      containerEl.removeEventListener('touchend', this._onTouchEndBound, true);
    }
    this._mouseDownPos = null;
    document.removeEventListener('keydown', this._onKeyDownBound);

    // Re-enable face selection
    if (this.facePicker) this.facePicker.setEnabled(true);
    // Restore the previously active cuboid tool (only on explicit cancel, not external tool select)
    if (restoreTool && this.editor && this._previousTool) {
      this.editor.setTool(this._previousTool);
    }
    this._previousTool = null;
  }

  /**
   * Handle keyboard during placement
   * @private
   */
  _onKeyDown(event) {
    if (event.key === 'Escape') {
      this._cancelPlacement();
    }
  }

  /**
   * Handle mousedown during placement
   * @private
   */
  _onMouseDown(event) {
    if (!this._placing) return;
    if (event.button !== 0) return;
    this._mouseDownPos = { x: event.clientX, y: event.clientY };
  }

  /**
   * Handle mouseup during placement
   * @private
   */
  _onMouseUp(event) {
    if (!this._placing) return;
    if (event.button !== 0) return;
    if (!this._mouseDownPos) return;

    const dx = event.clientX - this._mouseDownPos.x;
    const dy = event.clientY - this._mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._mouseDownPos = null;

    if (distance > this._CLICK_THRESHOLD) return;

    this._handlePlacementClick(event.clientX, event.clientY, event.target);
  }

  /**
   * Handle touchstart during placement
   * @private
   */
  _onTouchStart(event) {
    if (!this._placing) return;
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    this._mouseDownPos = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle touchend during placement
   * @private
   */
  _onTouchEnd(event) {
    if (!this._placing) return;
    if (!this._mouseDownPos) return;
    if (event.changedTouches.length === 0) {
      this._mouseDownPos = null;
      return;
    }

    const touch = event.changedTouches[0];
    const dx = touch.clientX - this._mouseDownPos.x;
    const dy = touch.clientY - this._mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._mouseDownPos = null;

    if (distance > this._CLICK_THRESHOLD) return;

    this._handlePlacementClick(touch.clientX, touch.clientY, event.target);
  }

  /**
   * Process a placement click at the given screen coordinates
   * @private
   */
  _handlePlacementClick(clientX, clientY, target) {
    const faceEl = target.closest ? target.closest('.face') : null;
    if (!faceEl) return;

    if (target.closest('.sidebar-panel') || target.closest('.info-panel')) return;

    const cubeEl = faceEl.closest('.cube');
    if (!cubeEl) return;

    const cuboid = this.world.findCuboidByElement(cubeEl);
    if (!cuboid) return;

    let effectiveCuboid = cuboid.isFragment
      ? this.world.cuboids.get(cuboid.parentKey) || cuboid
      : cuboid;

    // If the clicked cuboid is non-physical (water, gas), find the solid surface below
    if (effectiveCuboid.hasNoCollision && effectiveCuboid.hasNoCollision()) {
      const clickPos = this._resolveClickPosition(effectiveCuboid, clientX, clientY);
      if (!clickPos) return;

      const solidBelow = this._findSolidSurfaceBelow(clickPos.x, clickPos.z, effectiveCuboid.y + effectiveCuboid.height);
      if (!solidBelow) {
        this._statusText.textContent = 'No solid surface below water.';
        return;
      }
      effectiveCuboid = solidBelow;
    }

    // Detect which face was clicked to handle side-face placement
    const faceName = this._getFaceName(faceEl);

    const pos = this._resolveClickPosition(effectiveCuboid, clientX, clientY, faceName);
    if (!pos) return;

    this._placeImpostor(pos);
  }

  /**
   * Get the face name from a face element's class list
   * @private
   */
  _getFaceName(faceEl) {
    for (const name of ['top', 'front', 'back', 'left', 'right', 'bottom']) {
      if (faceEl.classList.contains(name)) return name;
    }
    return 'top';
  }

  /**
   * Resolve click to a placement position on top of a cuboid.
   * For side faces, projects the click onto the top face at the clicked edge.
   * @private
   */
  _resolveClickPosition(cuboid, clientX, clientY, faceName = 'top') {
    const CUBE_SIZE = this.camera.CUBE_SIZE;
    const screenX = clientX - window.innerWidth / 2;
    const screenY = clientY - window.innerHeight / 2;
    const renderingYOffset = CUBE_SIZE / 2;
    const adjustedScreenY = screenY - renderingYOffset * this.camera.zoomLevel;

    const topY = cuboid.y + cuboid.height;
    const faceWorldY = topY * CUBE_SIZE;
    const worldPos = Transform.screenToWorld(screenX, adjustedScreenY, this.camera, faceWorldY);

    let worldX = worldPos.x / CUBE_SIZE;
    let worldZ = -worldPos.z / CUBE_SIZE;

    const margin = 0.3;

    // For side faces, snap the perpendicular axis to the clicked edge
    // but keep the parallel axis from the click projection
    if (faceName === 'front') {
      // Front face: Z is at cuboid.z (min Z edge), keep X from click
      worldZ = cuboid.z + margin;
    } else if (faceName === 'back') {
      // Back face: Z is at cuboid.z + depth (max Z edge), keep X from click
      worldZ = cuboid.z + cuboid.depth - margin;
    } else if (faceName === 'left') {
      // Left face: X is at cuboid.x (min X edge), keep Z from click
      worldX = cuboid.x + margin;
    } else if (faceName === 'right') {
      // Right face: X is at cuboid.x + width (max X edge), keep Z from click
      worldX = cuboid.x + cuboid.width - margin;
    }

    // Clamp to cuboid bounds
    const x = Math.max(cuboid.x + margin, Math.min(cuboid.x + cuboid.width - margin, worldX));
    const z = Math.max(cuboid.z + margin, Math.min(cuboid.z + cuboid.depth - margin, worldZ));

    return { x, y: topY, z };
  }

  /**
   * Find the highest solid cuboid whose top surface is at or below maxY
   * at the given world XZ position.
   * @private
   */
  _findSolidSurfaceBelow(worldX, worldZ, maxY) {
    let best = null;
    let bestTopY = -Infinity;

    for (const cuboid of this.world.cuboids.values()) {
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;
      const topY = cuboid.y + cuboid.height;
      if (topY > maxY + 0.01) continue;
      if (topY <= bestTopY) continue;

      // Check if position falls within cuboid XZ bounds
      if (worldX >= cuboid.x && worldX < cuboid.x + cuboid.width &&
          worldZ >= cuboid.z && worldZ < cuboid.z + cuboid.depth) {
        best = cuboid;
        bestTopY = topY;
      }
    }
    return best;
  }

  /**
   * Place an impostor at the given position
   * @private
   */
  _placeImpostor(pos) {
    const tex = this._selectedTexture;
    if (!tex) return;

    const scale = parseFloat(this._scaleSlider.value) || 1;

    // Check that the impostor's virtual sorting cuboid (1x1x1) won't intersect any world cuboid
    if (this.world.wouldImpostorIntersect(pos)) {
      this._statusText.textContent = 'Too close to a wall — move further away.';
      return;
    }

    // Prevent placing exact duplicates (anti double-click spam)
    const MIN_DISTANCE = 0.05;
    for (const existing of this.world.impostors.values()) {
      const dx = existing.x - pos.x;
      const dz = existing.z - pos.z;
      const dy = existing.y - pos.y;
      if (dx * dx + dz * dz + dy * dy < MIN_DISTANCE * MIN_DISTANCE) {
        return; // Silently reject — feels like a double-click
      }
    }

    // Determine flip based on mode
    const flipped = this._flipMode === 'flipped' ? true
      : this._flipMode === 'normal' ? false
      : Math.random() < 0.5;

    const impostor = new Impostor({
      x: pos.x,
      y: pos.y,
      z: pos.z,
      textureUrl: tex.medium || tex.path,
      scale,
      flipped
    });
    impostor._isVegetation = true;

    this.world.addImpostor(impostor);
    impostor.sampleAO();

    // Record undo action
    if (this.undoManager) {
      const snapshot = impostor.toJSON();
      const world = this.world;
      const tool = this;
      this.undoManager.push({
        description: 'Place vegetation',
        undo: () => { world.removeImpostor(snapshot.id); world.update(); tool.refreshList(); },
        redo: () => {
          const imp = new Impostor(snapshot);
          imp._isVegetation = true;
          world.addImpostor(imp);
          imp.sampleAO();
          world.update();
          tool.refreshList();
        }
      });
    }

    this._statusText.textContent = `Placed ${tex.label}. Click again or Cancel.`;
    Debug.log('ImpostorTool', `Placed impostor ${impostor.id} at (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`);

    this.refreshList();
  }

  /**
   * Refresh the impostor list in the sidebar
   */
  refreshList() {
    const impostors = [...this.world.impostors.values()];
    if (impostors.length === 0) {
      this._listEl.innerHTML = '<div style="font-size: 11px; color: #666; padding: 4px 0;">No vegetation placed</div>';
      // Clear selection if the selected impostor was removed
      if (this._selectedImpostor && !this.world.impostors.has(this._selectedImpostor.id)) {
        this._deselectImpostor();
      }
      return;
    }

    const selectedId = this._selectedImpostor?.id;
    let html = '';
    for (const imp of impostors) {
      const label = imp.textureUrl.split('/').pop().replace('.webp', '').replace(/_256/g, '').replace(/_/g, ' ');
      const isSelected = imp.id === selectedId;
      html += `
        <div class="impostor-list-item" data-impostor-id="${imp.id}" style="display: flex; align-items: center; justify-content: space-between; padding: 3px 4px; font-size: 11px; border-bottom: 1px solid #333; cursor: pointer; background: ${isSelected ? '#2a4a6a' : 'transparent'}; border-radius: 3px;">
          <span style="color: #ccc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;" title="${label}">${label}</span>
          <button class="gui-button gui-button-danger impostor-remove-btn" data-impostor-id="${imp.id}" style="padding: 1px 6px; font-size: 10px; min-width: auto;">X</button>
        </div>
      `;
    }
    this._listEl.innerHTML = html;

    // Attach click-to-select and remove handlers
    this._listEl.querySelectorAll('.impostor-list-item').forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't select when clicking the remove button
        if (e.target.classList.contains('impostor-remove-btn')) return;
        this._selectImpostor(item.dataset.impostorId);
      });
    });
    this._listEl.querySelectorAll('.impostor-remove-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = e.target.dataset.impostorId;

        // Capture state for undo before removal
        const impostor = this.world.impostors.get(id);
        const snapshot = impostor ? impostor.toJSON() : null;

        if (this._selectedImpostor?.id === id) {
          this._deselectImpostor();
        }
        this.world.removeImpostor(id);

        if (this.undoManager && snapshot) {
          const world = this.world;
          const tool = this;
          this.undoManager.push({
            description: 'Remove vegetation',
            undo: () => {
              const imp = new Impostor(snapshot);
              imp._isVegetation = true;
              world.addImpostor(imp);
              imp.sampleAO();
              world.update();
              tool.refreshList();
            },
            redo: () => { world.removeImpostor(snapshot.id); world.update(); tool.refreshList(); }
          });
        }

        this.refreshList();
      });
    });
  }

  /**
   * Select an impostor by ID — highlight in scene and show action panel
   * @param {string} id
   */
  _selectImpostor(id) {
    // Deselect previous
    if (this._selectedImpostor) {
      this._selectedImpostor.setSelected(false);
    }

    const impostor = this.world.getImpostor(id);
    if (!impostor) return;

    this._selectedImpostor = impostor;
    impostor.setSelected(true);

    // Update selection panel
    const label = impostor.textureUrl.split('/').pop().replace('.webp', '').replace(/_256/g, '').replace(/_/g, ' ');
    this._selectionName.textContent = label;
    this._selectionPanel.style.display = '';

    // Update list item highlights
    this._listEl.querySelectorAll('.impostor-list-item').forEach(item => {
      item.style.background = item.dataset.impostorId === id ? '#2a4a6a' : 'transparent';
    });
  }

  /**
   * Clear impostor selection
   */
  _deselectImpostor() {
    if (this._selectedImpostor) {
      this._selectedImpostor.setSelected(false);
      this._selectedImpostor = null;
    }
    this._selectionPanel.style.display = 'none';

    // Clear list highlights
    this._listEl.querySelectorAll('.impostor-list-item').forEach(item => {
      item.style.background = 'transparent';
    });
  }

  /**
   * Apply the currently selected gallery texture + scale to the selected impostor
   * @private
   */
  _applyTextureToSelected() {
    if (!this._selectedImpostor || !this._selectedTexture) return;

    const tex = this._selectedTexture;
    const scale = parseFloat(this._scaleSlider.value) || 1;
    const imp = this._selectedImpostor;

    // Capture before state for undo
    const oldUrl = imp.textureUrl;
    const oldScale = imp.scale;
    const newUrl = tex.medium || tex.path;
    const newScale = scale;

    imp.textureUrl = newUrl;
    imp.scale = newScale;
    imp.element.style.backgroundImage = `url('${imp.textureUrl}')`;

    if (this.undoManager) {
      const impId = imp.id;
      const world = this.world;
      const tool = this;
      this.undoManager.push({
        description: 'Change vegetation texture',
        undo: () => {
          const i = world.impostors.get(impId);
          if (i) {
            i.textureUrl = oldUrl;
            i.scale = oldScale;
            if (i.element) i.element.style.backgroundImage = `url('${oldUrl}')`;
          }
          world.forceRender();
          tool.refreshList();
        },
        redo: () => {
          const i = world.impostors.get(impId);
          if (i) {
            i.textureUrl = newUrl;
            i.scale = newScale;
            if (i.element) i.element.style.backgroundImage = `url('${newUrl}')`;
          }
          world.forceRender();
          tool.refreshList();
        }
      });
    }

    this._statusText.textContent = `Applied ${tex.label} to selected impostor.`;
    this.world.forceRender();
    this.refreshList();
  }

  /**
   * Delete the currently selected impostor
   * @private
   */
  _deleteSelected() {
    if (!this._selectedImpostor) return;
    const snapshot = this._selectedImpostor.toJSON();
    const id = this._selectedImpostor.id;
    this._deselectImpostor();
    this.world.removeImpostor(id);

    if (this.undoManager) {
      const world = this.world;
      const tool = this;
      this.undoManager.push({
        description: 'Delete vegetation',
        undo: () => {
          const imp = new Impostor(snapshot);
          imp._isVegetation = true;
          world.addImpostor(imp);
          imp.sampleAO();
          world.update();
          tool.refreshList();
        },
        redo: () => { world.removeImpostor(snapshot.id); world.update(); tool.refreshList(); }
      });
    }

    this.refreshList();
  }
}
